import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { authApi, errMsg } from "../api";
import { FormField, PrimaryButton } from "./FormField";

const TITLES = {
  login: "Operator Sign In",
  register: "Create Account",
  forgot: "Reset Password",
};

const DESCRIPTIONS = {
  login: "Sign in to check vehicles in and out and manage assignments.",
  register: "Create an operator account to manage the lot.",
  forgot: "Enter your email and we'll send you a link to choose a new password.",
};

export const AuthDialog = () => {
  const { authOpen, setAuthOpen, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [values, setValues] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

  const onOpenChange = (open) => {
    setAuthOpen(open);
    if (open) setMode("login");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        await register({ email: values.email, password: values.password, name: values.name || null });
        toast.success("Account created — you're signed in");
      } else if (mode === "forgot") {
        await authApi.forgotPassword(values.email);
        toast.success("If that email has an account, a reset link is on its way");
        setMode("login");
      } else {
        await login({ email: values.email, password: values.password });
        toast.success("Signed in");
      }
      setValues({ email: "", password: "", name: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#262626] rounded-sm" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">{TITLES[mode]}</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">{DESCRIPTIONS[mode]}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {mode === "register" && (
            <FormField
              label="Name (optional)"
              data-testid="auth-name-input"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Mike"
            />
          )}
          <FormField
            label="Email"
            type="email"
            required
            data-testid="auth-email-input"
            value={values.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@example.com"
          />
          {mode !== "forgot" && (
            <FormField
              label="Password"
              type="password"
              required
              data-testid="auth-password-input"
              value={values.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder="••••••••"
            />
          )}
          <PrimaryButton type="submit" data-testid="auth-submit-btn" disabled={busy}>
            {mode === "register" ? "Create Account" : mode === "forgot" ? "Send Reset Link" : "Sign In"}
          </PrimaryButton>
          <div className="flex flex-col gap-2 items-start">
            <button
              type="button"
              data-testid="auth-toggle-mode-btn"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="text-sm text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
            >
              {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
            {mode !== "forgot" && (
              <button
                type="button"
                data-testid="auth-forgot-password-btn"
                onClick={() => setMode("forgot")}
                className="text-sm text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
              >
                Forgot your password?
              </button>
            )}
            {mode === "forgot" && (
              <button
                type="button"
                data-testid="auth-back-to-login-btn"
                onClick={() => setMode("login")}
                className="text-sm text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors"
              >
                Back to sign in
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
